import { useMemo } from 'react';
import { useGetAppInitDetails } from './use-get-app-init-details';

const CSB_PRODUCTS = ['medsupp', 'dvh', 'shl'] as const;
const MEDICARE_PRODUCTS = ['ma', 'mapd', 'pdp'] as const;

interface SegmentationResult {
  isCSBSegment: boolean;
  isMedicareSegment: boolean;
  isMixedSegment: boolean;
  isAddressEditable: boolean;
  showPaperlessSetup: boolean;
  showPDPPremium: boolean;
  showSupplementalAutoPay: boolean;
  showMedicarePremium: boolean;
  showPharmacySettings: boolean;
  showFormsCenter: boolean;
  productSubTypes: string[];
}

/**
 * Custom hook to determine product segmentation based on app initialization details.
 * @returns {SegmentationResult} Object containing segment flags and product subtypes (lowercase).
 */
export const useProductSegmentation = (): SegmentationResult => {
  const { data: appInitDetails } = useGetAppInitDetails();

  const productSubTypes = useMemo(
    () => appInitDetails?.segmentation?.productSubTypes ?? [],
    [appInitDetails],
  );

  const segmentation = useMemo(() => {
    if (!appInitDetails?.segmentation) {
      return {
        isCSBSegment: false,
        isMedicareSegment: false,
        isMixedSegment: false,
        isAddressEditable: false,
        showPaperlessSetup: false,
        showPDPPremium: false,
        showSupplementalAutoPay: false,
        showMedicarePremium: false,
        showPharmacySettings: false,
        showFormsCenter: false,
        productSubTypes: [],
      };
    }

    const normalizedProductSubTypes = productSubTypes.map((type) =>
      type.toLowerCase(),
    );

    const hasCSB = normalizedProductSubTypes.some((type) =>
      CSB_PRODUCTS.includes(type as (typeof CSB_PRODUCTS)[number]),
    );
    const hasMedicare = normalizedProductSubTypes.some((type) =>
      MEDICARE_PRODUCTS.includes(type as (typeof MEDICARE_PRODUCTS)[number]),
    );

    const isCSBSegment = hasCSB && !hasMedicare;
    const isMedicareSegment = hasMedicare && !hasCSB;
    const isMixedSegment = hasCSB && hasMedicare;

    const isAddressEditable = hasCSB;
    const showPaperlessSetup = hasMedicare;

    // Check if user has MAPD or PDP products for pharmacy settings
    const hasMAPDOrPDP = normalizedProductSubTypes.some((type) =>
      ['mapd', 'pdp'].includes(type),
    );

    return {
      isCSBSegment,
      isMedicareSegment,
      isMixedSegment,
      isAddressEditable,
      showPaperlessSetup,
      showPDPPremium:
        appInitDetails.segmentation.payYourPdpPremiumEnabled ?? false,
      showSupplementalAutoPay:
        appInitDetails.segmentation.manageSupplementalAutoPayEnabled ?? false,
      showMedicarePremium:
        appInitDetails.segmentation.payYourMedicarePremiumEnabled ?? false,
      showPharmacySettings: hasMAPDOrPDP,
      showFormsCenter: appInitDetails.segmentation.formCenterEnabled ?? false,
      productSubTypes: normalizedProductSubTypes,
    };
  }, [productSubTypes, appInitDetails]);

  return segmentation;
};
